import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartParametersModalLauncherBlockConfig } from '@front-platform/content-builder';
import { cartParametersModalLauncherBlockResolveConfig } from '@front-platform/content-builder';
import { Component, DestroyRef, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';

import { CartKeys } from '@config/keys/cart.keys';
import { ShopKeys } from '@config/keys/shop.keys';

import { DoodCartModel } from '@core/models/cart.model';
import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { SettingsParametersState } from '@store/settings/settings.state';
import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
export interface CartParametersModalLauncherBlockRuntimeConfig {}

export type CartParametersModalLauncherBlockConfigInput = Partial<
  Omit<
    CartParametersModalLauncherBlockConfig,
    keyof CartParametersModalLauncherBlockRuntimeConfig
  > &
    CartParametersModalLauncherBlockRuntimeConfig
>;
export type CartParametersModalLauncherBlockResolvedConfig = Omit<
  CartParametersModalLauncherBlockConfig,
  keyof CartParametersModalLauncherBlockRuntimeConfig
> &
  Partial<CartParametersModalLauncherBlockRuntimeConfig>;
export interface CartParametersModalLauncherBlockContract extends ConfigComponentContract<
  CartParametersModalLauncherBlockConfigInput,
  CartParametersModalLauncherBlockResolvedConfig
> {}
import {
  resolveAuthModeForOrderType,
  resolveOrderTypeForShop,
} from '@shared/utils/distribution-mode/distribution-mode-from-context.util';

const CART_PARAMETERS_MODAL_PROMPTED_SHOP_IDS_KEY = 'cart_parameters_modal_prompted_shop_ids';

@Component({
  template: ``,
  selector: 'app-cart-parameters-modal-launcher-block',
  standalone: false,
})
export class CartParametersModalLauncherBlockComponent implements CartParametersModalLauncherBlockContract {
  readonly config = input<CartParametersModalLauncherBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartParametersModalLauncherBlockConfig,
    CartParametersModalLauncherBlockRuntimeConfig
  >(cartParametersModalLauncherBlockResolveConfig, () => ({ config: this.config() }));
  static handle = 'CartParametersModalLauncherBlockComponent';

  constructor(
    private cartSelector: CartStoreSelector,
    private cartDispatcher: CartStoreDispatcher,
    private shopSelector: ShopStoreSelector,
    private settingsSelector: SettingsStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly cartService: CartService,
    private readonly modalsService: ModalsService,
    private readonly shopSearchParametersService: ShopSearchParametersService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.shopSelector.selectActive$
      .pipe(
        map(shop => shop?.[ShopKeys.Id] ?? null),
        distinctUntilChanged(),
        filter((shopId): shopId is string => !!shopId),
        switchMap(shopId =>
          combineLatest([
            this.settingsSelector.selectParameters$,
            this.cartSelector.selectActive$,
          ]).pipe(
            filter(([, cart]) => this.isReadyToOpen(cart, shopId)),
            take(1),
            switchMap(([settings, cart]) =>
              this.cartService
                .getCartEntityDistributionModes$(this.marketplaceSelector.marketplace, shopId)
                .pipe(
                  take(1),
                  map(allowedDistributionModes => ({
                    shopId,
                    settings,
                    cart,
                    allowedHandles: allowedDistributionModes.map(item => item.value),
                  })),
                ),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ shopId, settings, cart, allowedHandles }) => {
        if (this.hasPromptedForShop(shopId)) {
          return;
        }

        if (!this.shouldPromptCartParameters(settings, allowedHandles)) {
          return;
        }

        if (!this.hasValidDistributionMode(cart, allowedHandles)) {
          this.ensureCartHasDistributionMode(cart, settings, allowedHandles);
        }

        this.prepareSettingsForModalOpen(settings, allowedHandles);

        this.markShopAsPrompted(shopId);
        this.modalsService.open(CartParametersModalComponent.handle);
      });
  }

  private isReadyToOpen(cart: DoodCartModel | null, shopId: string): boolean {
    return !!cart?.[CartKeys.Handle] && cart[CartKeys.Shop] === shopId;
  }

  private shouldPromptCartParameters(
    settings: SettingsParametersState,
    allowedHandles: string[],
  ): boolean {
    if (settings.is_distribution_mode_defined) {
      return true;
    }

    return allowedHandles.length === 1;
  }

  private prepareSettingsForModalOpen(
    settings: SettingsParametersState,
    allowedHandles: string[],
  ): void {
    if (settings.is_distribution_mode_defined || allowedHandles.length !== 1) {
      return;
    }

    // Prevent cart-parameters modal from auto-submitting on single-mode shops.
    this.shopSearchParametersService.setDistributionModeType(allowedHandles[0], true);
  }

  private hasValidDistributionMode(cart: DoodCartModel | null, allowedHandles: string[]): boolean {
    return !!cart?.[CartKeys.Type] && allowedHandles.includes(cart[CartKeys.Type]);
  }

  private ensureCartHasDistributionMode(
    cart: DoodCartModel | null,
    settings: SettingsParametersState,
    allowedHandles: string[],
  ): void {
    if (!cart || !allowedHandles.length) {
      return;
    }

    const orderType = resolveOrderTypeForShop(allowedHandles, settings);
    const shop = this.shopSelector.active;
    const marketplace = this.marketplaceSelector.marketplace;
    const authMode = shop ? resolveAuthModeForOrderType(marketplace, shop, orderType) : undefined;

    this.cartDispatcher.updateActive({
      [CartKeys.Type]: orderType,
      ...(authMode !== undefined && { [CartKeys.AuthMode]: authMode }),
    });
  }

  private hasPromptedForShop(shopId: string): boolean {
    return this.getPromptedShopIds().includes(shopId);
  }

  private markShopAsPrompted(shopId: string): void {
    const shopIds = this.getPromptedShopIds();
    if (shopIds.includes(shopId)) {
      return;
    }

    this.savePromptedShopIds([...shopIds, shopId]);
  }

  private getPromptedShopIds(): string[] {
    if (typeof sessionStorage === 'undefined') {
      return [];
    }

    try {
      const stored = sessionStorage.getItem(CART_PARAMETERS_MODAL_PROMPTED_SHOP_IDS_KEY);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === 'string')
        : [];
    } catch {
      return [];
    }
  }

  private savePromptedShopIds(shopIds: string[]): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.setItem(CART_PARAMETERS_MODAL_PROMPTED_SHOP_IDS_KEY, JSON.stringify(shopIds));
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartParametersModalLauncherBlockComponent, {
  'keyAliases': {},
});
